export const APP_TIMEZONE = import.meta.env.VITE_APP_TIMEZONE || 'Asia/Yekaterinburg'

function zonedParts(value, includeTime = false) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const options = {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } : {}),
  }
  return Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', options)
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  )
}

export function toDateInput(value = new Date()) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const parts = zonedParts(value)
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : ''
}

export function toDateTimeInput(value) {
  if (!value) return ''
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(value)) {
    return value.slice(0, 16)
  }
  const parts = zonedParts(value, true)
  return parts ? `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}` : ''
}

export function toApiDateTime(value) {
  // The API interprets offset-free date-times in the configured resort timezone.
  // Sending the form's wall-clock value prevents the operator's browser timezone
  // from silently shifting a booking.
  return value || null
}

export function formatDate(value, options = {}) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: APP_TIMEZONE,
    day: 'numeric',
    month: 'short',
    ...options,
  }).format(date)
}

export function formatDateTime(value) {
  return formatDate(value, { hour: '2-digit', minute: '2-digit' })
}

export function formatTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: APP_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

export function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—'
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value))
}

export function addDays(value, amount) {
  const date = value instanceof Date ? new Date(value) : new Date(value)
  date.setDate(date.getDate() + amount)
  return date
}

export function startOfWeek(value = new Date()) {
  const calendarDate = toDateInput(value)
  const date = new Date(`${calendarDate}T12:00:00`)
  const day = date.getDay() || 7
  date.setDate(date.getDate() - day + 1)
  return date
}

export function sameDay(left, right) {
  return toDateInput(left) === toDateInput(right)
}

export function bookingOverlapsDay(booking, day) {
  const start = new Date(booking.startsAt)
  const end = new Date(booking.endsAt)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
  const dayKey = toDateInput(day)
  return toDateInput(start) <= dayKey && toDateInput(new Date(end.getTime() - 1)) >= dayKey
}

export function initials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'К'
}
